<template>
	<Col class="height-100">
		<BeforeShareRecommendationsMainContent
			class="pt-4"
			:workflow="workflow"
			@share="shareFeature"
			v-show="showMainContent"
			:isLocked.sync="isLocked"
			:isVisible.sync="isVisible"
			:isLoadingShare="isLoadingShare"
			:notifyByMail.sync="notifyByMail"
			:hasFeatureDescription="hasFeatureDescription"
			:showEditFeatureDescription.sync="showEditFeatureDescription"
			@edit-feature-description="showEditFeatureDescription = true"
			@reassign-connections="showReassignConnectionsSuggestions = true"
		>
			<template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope">
				<!-- Expose BeforeShareRecommendationsMainContent inner slots, to the father's component of this one -->
				<slot :name="slot" v-bind="scope" />
			</template>
		</BeforeShareRecommendationsMainContent>
		<BeforeShareRecommendationPageContent
			v-if="!showMainContent"
			@back="goBack"
			class="px-5 pt-4"
			v-on="$listeners"
			:workflow="workflow"
			:showEditFeatureDescription="showEditFeatureDescription"
			:showReassignConnectionsSuggestions="showReassignConnectionsSuggestions"
		/>
	</Col>
</template>
<script>
import isString from 'lodash/isString';

import { AccountPermissions, WorkflowAccessType } from '@ply-io/ply-front-shared-utilities';

import BeforeShareRecommendationPageContent from '@/components/dialogs/ShareFeatureDialog/BeforeShareRecommendations/BeforeShareRecommendationPageContent.vue';
import BeforeShareRecommendationsMainContent from '@/components/dialogs/ShareFeatureDialog/BeforeShareRecommendations/BeforeShareRecommendationsMainContent.vue';
import { OPTIONS_MAP } from '@/components/dialogs/ShareModuleDialog/share-module-dialog.const.js';
import { updateRoles } from '@/store/modules/account/actions';
import { hasAccountPermission } from '@/store/modules/account/getters';
import { shareWorkflow, updateWorkflow } from '@/store/modules/workflows/actions';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
export default {
	name: 'BeforeShareRecommendations',
	components: {
		BeforeShareRecommendationPageContent,
		BeforeShareRecommendationsMainContent,
	},
	props: {
		service: { type: Object },
		workflow: { type: Object },
		inviteUserRole: { type: String },
		shareWithSelection: { type: Array },
		roleToAutoUpgrade: { type: String },
		usersWithBadBillingLevel: { type: Array },
	},
	computed: {
		...mapGettersShorthand({ hasAccountPermission }),
		showMainContent() {
			return !this.showEditFeatureDescription && !this.showReassignConnectionsSuggestions;
		},
		workflowId() {
			return this.workflow.id;
		},
		hasFeatureDescription() {
			return this.onBoardingText?.length > 0;
		},
		onBoardingText() {
			return this.workflow.onboarding_settings?.text;
		},
		allUsersToShareWith() {
			const emailsToInvite = this.canInviteUsers ? this.emailsToInvite.map((e) => ({ email: e })) : [];
			return [...this.usersSelected, ...emailsToInvite];
		},
		allGroupsToShareWith() {
			return this.existingUsersOrGroupsSelected.filter((gr) => gr.isGroup && !gr.isEveryone);
		},
		allGroupsIdsToShareWith() {
			return this.allGroupsToShareWith.map((gr) => gr.id);
		},
		isTryingToShareWithEveryone() {
			return this.existingUsersOrGroupsSelected.some((gr) => gr.isEveryone);
		},
		emailsToInvite() {
			return this.shareWithSelection.filter((u) => isString(u));
		},
		existingUsersOrGroupsSelected() {
			return this.shareWithSelection.filter((u) => !isString(u));
		},
		usersSelected() {
			return this.existingUsersOrGroupsSelected.filter((x) => x.isUser);
		},
		canInviteUsers() {
			return this.hasAccountPermission(AccountPermissions.InviteUsers);
		},
		isWorkflowSharedWithEveryone() {
			return this.workflow.access_type === WorkflowAccessType.WorkspaceUsers;
		},
		needToUpdateWorkflowAccessType() {
			return this.isTryingToShareWithEveryone !== this.isWorkflowSharedWithEveryone;
		},
		newAccessTypeForWorkflow() {
			return this.isTryingToShareWithEveryone ? WorkflowAccessType.WorkspaceUsers : WorkflowAccessType.SharedUsersOnly;
		},
	},
	methods: {
		...mapActionsShorthand({ shareWorkflow, updateWorkflow, updateRoles }),
		async autoUpgradeMembers() {
			try {
				await this.updateRoles({ users: this.usersWithBadBillingLevel.map(({ id }) => id), roleCode: this.roleToAutoUpgrade });
				return true;
			} catch (e) {
				this.$toast.error(
					'Error occurred while upgrading members, if this error persists, remove the auto-upgraded members from the share process'
				);
				return false;
			}
		},
		async shareFeature() {
			this.isLoadingShare = true;
			try {
				if (this.usersWithBadBillingLevel.length > 0) {
					const didAutoUpgradeSuccessfully = await this.autoUpgradeMembers();
					if (!didAutoUpgradeSuccessfully) {
						this.isLoadingShare = false;
						return;
					}
				}
				const res = await this.shareWorkflow({
					isOn: this.isVisible,
					flowId: this.workflowId,
					isSendMail: this.notifyByMail,
					users: this.allUsersToShareWith,
					onboardingText: this.onBoardingText,
					inviteUserRole: this.inviteUserRole,
					groups: this.allGroupsIdsToShareWith,
					role: /*this.isLocked ? OPTIONS_MAP.CAN_USE_ENFORCED :*/ OPTIONS_MAP.CAN_USE,
				});
				if (this.needToUpdateWorkflowAccessType) {
					await this.updateWorkflow({ data: { access_type: this.newAccessTypeForWorkflow }, id: this.workflowId });
				}
				if (res.success) {
					this.$emit('shared');
					this.$toast.success('Shared Successfully');
				} else {
					this.$toast.error('Sharing Failed');
				}
			} catch (e) {
				console.error('Error sharing feature', e);
				this.$toast.error('Error occurred while sharing feature, please try again later');
			} finally {
				this.isLoadingShare = false;
			}
		},
		goBack() {
			this.showEditFeatureDescription = false;
			this.showReassignConnectionsSuggestions = false;
		},
	},
	data() {
		return {
			isLocked: false,
			isVisible: true,
			notifyByMail: true,
			isLoadingShare: false,
			showEditFeatureDescription: false,
			showReassignConnectionsSuggestions: false,
		};
	},
};
</script>
<style scoped lang="scss"></style>
